import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface FaqFeedbackRow {
  q_key: string;
  category: string | null;
  helpful: boolean;
  created_at: string;
}

interface UseFaqFeedbackResult {
  rows: FaqFeedbackRow[];
  loading: boolean;
  loadError: boolean;
  reload: () => void;
}

/**
 * Loads raw FAQ feedback records. Aggregation happens in the view layer so the
 * summary can be sliced by category or sorted without extra round-trips.
 */
export function useFaqFeedback(): UseFaqFeedbackResult {
  const [rows, setRows] = useState<FaqFeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data, error } = await supabase
        .from("faq_feedback")
        .select("q_key, category, helpful, created_at")
        .order("created_at", { ascending: false })
        .limit(3000);
      if (error) throw error;
      setRows((data ?? []) as FaqFeedbackRow[]);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { rows, loading, loadError, reload: load };
}